/* ---------------------------------------------------------------------------
   Google Picker — הבעלים בוחר חשבוניות והצעות מחיר מה-Drive.

   עם ההרשאה `drive.file` זו הדרך *היחידה* שהשרת יוכל לקרוא קובץ קיים:
   מה שנבחר כאן נפתח לאפליקציה, ושום דבר אחר ב-Drive לא.
   --------------------------------------------------------------------------- */

import i18n, { currentLocale } from '@/lib/i18n';

interface PickerDoc {
  id: string;
  name: string;
}

interface PickerResponse {
  action: string;
  docs?: PickerDoc[];
}

interface PickerBuilder {
  addView(view: unknown): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(id: string): PickerBuilder;
  setLocale(locale: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  setCallback(cb: (res: PickerResponse) => void): PickerBuilder;
  build(): { setVisible(v: boolean): void };
}

interface GooglePickerNamespace {
  PickerBuilder: new () => PickerBuilder;
  DocsView: new (viewId?: string) => {
    setMimeTypes(types: string): unknown;
    setIncludeFolders(v: boolean): unknown;
  };
  ViewId: { DOCS: string };
  Feature: { MULTISELECT_ENABLED: string };
  Action: { PICKED: string; CANCEL: string };
}

/** Google Identity Services — הדפדפן מבקש טוקן משלו, מוגבל לקבצים שנבחרו. */
interface TokenClient {
  requestAccessToken(): void;
}
interface TokenResponse {
  access_token?: string;
  error?: string;
}

declare global {
  interface Window {
    gapi?: { load(name: string, cb: () => void): void };
    google?: {
      picker?: GooglePickerNamespace;
      accounts?: {
        oauth2: {
          initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (res: TokenResponse) => void;
            error_callback?: (err: { type?: string }) => void;
          }): TokenClient;
        };
      };
    };
  }
}

const SCRIPT = 'https://apis.google.com/js/api.js';
const GIS_SCRIPT = 'https://accounts.google.com/gsi/client';
/** רק הקבצים שהמשתמש יבחר כאן — לא המייל, לא שאר ה-Drive. */
const PICKER_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const MIME_TYPES = [
  'application/pdf',
  'text/csv',
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
].join(',');

let loading: Promise<GooglePickerNamespace> | null = null;
let gisLoading: Promise<void> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Could not load ${src}`));
    document.head.appendChild(el);
  });
}

/**
 * טוקן לבחירת הקבצים, ישירות מ-Google לדפדפן.
 *
 * למה לא טוקן מהשרת: לחיבור של העסק יש הסכמה אחת לשלוש הרשאות, כולל קריאה
 * ושליחה של מייל. טוקן כזה בדפדפן היה נותן לכל סקריפט בדף לקרוא את התיבה.
 * כאן הטוקן מוגבל ל-drive.file, חי דקות, ואינו נשמר בשום מקום.
 */
async function requestPickerToken(clientId: string): Promise<string | null> {
  gisLoading ??= loadScript(GIS_SCRIPT);
  await gisLoading;
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) throw new Error('Could not load Google sign-in');

  return new Promise((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: PICKER_SCOPE,
      callback: (res) => {
        if (res.access_token) resolve(res.access_token);
        // ביטול או סירוב — לא שגיאה, פשוט לא נבחרו קבצים.
        else if (res.error === 'access_denied' || res.error === 'popup_closed') resolve(null);
        else reject(new Error(res.error ?? 'Google did not return a token'));
      },
      error_callback: (err) =>
        err.type === 'popup_closed' ? resolve(null) : reject(new Error(err.type ?? 'Google sign-in failed')),
    });
    client.requestAccessToken();
  });
}

function loadPicker(): Promise<GooglePickerNamespace> {
  loading ??= new Promise((resolve, reject) => {
    const done = () =>
      window.gapi!.load('picker', () =>
        window.google?.picker
          ? resolve(window.google.picker)
          : reject(new Error('Picker failed to load')),
      );
    if (window.gapi) return done();
    loadScript(SCRIPT).then(done, (err: unknown) => {
      loading = null;
      reject(err instanceof Error ? err : new Error('Could not load Google Picker'));
    });
  });
  return loading;
}

/** פותח את ה-Picker ומחזיר את הקבצים שנבחרו (ריק = ביטול). */
export async function pickDriveFiles(config: {
  apiKey: string;
  appId: string;
  clientId: string;
}): Promise<PickerDoc[]> {
  const [picker, token] = await Promise.all([loadPicker(), requestPickerToken(config.clientId)]);
  if (!token) return [];
  return new Promise((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS);
    view.setMimeTypes(MIME_TYPES);
    view.setIncludeFolders(true);
    new picker.PickerBuilder()
      .addView(view)
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(token)
      .setDeveloperKey(config.apiKey)
      .setAppId(config.appId)
      .setLocale(currentLocale() === 'he' ? 'iw' : 'en')
      .setTitle(i18n.t('onboarding:drive.pickerTitle'))
      .setCallback((res) => {
        if (res.action === picker.Action.PICKED) resolve(res.docs ?? []);
        else if (res.action === picker.Action.CANCEL) resolve([]);
      })
      .build()
      .setVisible(true);
  });
}
